import React, { useEffect, useState } from 'react';
import { TrendingUp, Save, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCES = [
  { key: 'deal_enquiry', label: 'Deal Enquiry (deal pages)', desc: 'Leads who fill the 4-step enquiry on a deal page' },
  { key: 'contact_form', label: 'Contact Form (site)',       desc: 'Leads from the main "Contact Us" form' },
  { key: 'popup',        label: 'Popup Banner',               desc: 'Leads who subscribed via the homepage popup' },
];

export const AutomationTab = ({ getStoredPassword }) => {
  const [enabled, setEnabled] = useState(false);
  const [assignments, setAssignments] = useState({});
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API_URL}/api/admin/automation-rules?password=${getStoredPassword()}`),
          fetch(`${API_URL}/api/admin/team?password=${getStoredPassword()}`),
        ]);
        if (r1.ok) {
          const d = await r1.json();
          setEnabled(!!d.auto_assign_enabled);
          setAssignments(d.source_assignments || {});
        }
        if (r2.ok) setTeam(await r2.json());
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [getStoredPassword]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/automation-rules?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_assign_enabled: enabled, source_assignments: assignments }),
      });
      if (res.ok) toast.success('Automation rules saved');
      else toast.error('Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  if (loading) return <div className="text-white/50 text-sm">Loading automation rules…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl text-white font-light flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-[#D4AF37]" /> Automation Rules
        </h2>
        <p className="text-white/50 text-sm mt-1">
          Automatically assign new leads to team members based on where they came from. Rules run instantly on lead creation.
        </p>
      </div>

      {/* Master toggle */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="automation-master-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            className="mt-1 accent-[#D4AF37] w-4 h-4"
          />
          <div>
            <p className="text-white font-semibold text-sm">Enable auto-assignment</p>
            <p className="text-white/50 text-xs mt-0.5">
              Master switch. When off, no rules below run regardless of selection.
            </p>
          </div>
        </label>
      </div>

      {/* Source → Owner rules */}
      <div className={`space-y-3 ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
        <p className="text-white/60 text-xs uppercase tracking-widest font-semibold">Source → Owner</p>
        {SOURCES.map(s => (
          <div key={s.key} className="bg-white/5 border border-white/10 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">{s.label}</p>
              <p className="text-white/40 text-xs mt-0.5">{s.desc}</p>
            </div>
            <select
              value={assignments[s.key] || ''}
              onChange={e => setAssignments({ ...assignments, [s.key]: e.target.value || undefined })}
              className="bg-[#0a1a14] border border-white/10 text-white rounded-md px-3 py-2 text-sm min-w-[220px]"
              data-testid={`automation-source-${s.key}`}
            >
              <option value="">— Don't auto-assign —</option>
              {team.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {team.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2.5">
          <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
          <p className="text-yellow-200 text-xs">
            No team members yet — add some in the <strong>Team</strong> tab so you can assign rules.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="automation-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Rules'}
        </Button>
      </div>
    </div>
  );
};
